import type { CommitmentVsUsage, CommitmentVsUsageMonth, CommitmentVsUsageReportItem } from '@vy/types';
import ExcelJS from 'exceljs';
import {
  formatContractEnd,
  formatMonthHeader,
  formatNumber,
  formatPercent,
  formatReportDate,
  formatResetCell,
  formatResetCycle,
} from '../../../commitment-vs-usage-report-page/commitment-vs-usage-report-components/commitment-vs-usage-format-helpers';

const SHEET_NAME = 'Commitment vs Usage';

type ExportColumn = {
  field: string;
  group: string | null;
  header: string;
  getValue: (item: CommitmentVsUsageReportItem) => string;
};

function buildStaticColumns(): ExportColumn[] {
  return [
    { field: 'customer.name', group: null, header: 'Customer', getValue: (i) => i.customer.name },
    { field: 'customer.externalId', group: null, header: 'External ID', getValue: (i) => i.customer.externalId ?? '--' },
    { field: 'product.variantName', group: null, header: 'Product / Meter', getValue: (i) => i.product.variantName },
    { field: 'contract.name', group: null, header: 'Contract Name', getValue: (i) => i.contract.name },
    { field: 'contract.status', group: null, header: 'Status', getValue: (i) => i.contract.status },
    { field: 'contract.startDate', group: null, header: 'Contract Start', getValue: (i) => formatReportDate(i.contract.startDate) },
    { field: 'contract.endDate', group: null, header: 'Contract End', getValue: (i) => formatContractEnd(i.contract.endDate) },
    { field: 'contract.daysToEnd', group: null, header: 'Days to End', getValue: (i) => formatNumber(i.contract.daysToEnd) },
    { field: 'commitment.resetIntervalMonths', group: null, header: 'Reset Cycle', getValue: (i) => formatResetCycle(i.commitment.resetIntervalMonths) },
    { field: 'commitment.units', group: null, header: 'Qty Commitment', getValue: (i) => formatNumber(i.commitment.units) },
    { field: 'currentCycle.consumed', group: null, header: 'Consumed (Qty)', getValue: (i) => formatNumber(i.currentCycle.consumed) },
    { field: 'currentCycle.pctUsage', group: null, header: '% Usage', getValue: (i) => formatPercent(i.currentCycle.pctUsage) },
    { field: 'currentCycle.pacing', group: null, header: 'Pacing %', getValue: (i) => formatPercent(i.currentCycle.pacing) },
    { field: 'currentCycle.projectedEoP', group: null, header: 'Projected EoP (Qty)', getValue: (i) => formatNumber(i.currentCycle.projectedEoP) },
    { field: 'currentCycle.shortfall', group: null, header: 'Shortfall (Qty)', getValue: (i) => formatNumber(i.currentCycle.shortfall) },
    { field: 'currentCycle.overage', group: null, header: 'Overage (Qty)', getValue: (i) => formatNumber(i.currentCycle.overage) },
  ];
}

function buildMonthColumns(month: CommitmentVsUsageMonth, index: number): ExportColumn[] {
  const groupName = formatMonthHeader(month.startTime);
  return [
    { field: `monthly.${index}.qtyUsed`, group: groupName, header: 'Qty Used', getValue: (i) => formatNumber(i.monthlyEntries[index]?.qtyUsed) },
    { field: `monthly.${index}.cumulativeQty`, group: groupName, header: 'Cumul. Qty', getValue: (i) => formatNumber(i.monthlyEntries[index]?.cumulativeQty) },
    { field: `monthly.${index}.cumulativePct`, group: groupName, header: 'Cumul. %', getValue: (i) => formatPercent(i.monthlyEntries[index]?.cumulativePct) },
    { field: `monthly.${index}.reset`, group: groupName, header: 'Reset', getValue: (i) => formatResetCell(i.monthlyEntries[index]?.isResetMonth) },
  ];
}

function buildExportColumns(months: CommitmentVsUsageMonth[]): ExportColumn[] {
  return [...buildStaticColumns(), ...months.flatMap((month, index) => buildMonthColumns(month, index))];
}

type Group = { name: string | null; span: number };

function chunkGroups(columns: ExportColumn[]): Group[] {
  return columns.reduce<Group[]>((groups, column) => {
    const last = groups[groups.length - 1];
    if (last && last.name === column.group) {
      last.span += 1;
      return groups;
    }
    groups.push({ name: column.group, span: 1 });
    return groups;
  }, []);
}

export function buildCommitmentVsUsageExport(
  workbook: ExcelJS.Workbook,
  report: CommitmentVsUsage,
  hiddenFields?: ReadonlySet<string>,
): void {
  const allColumns = buildExportColumns(report.months);
  const columns = hiddenFields
    ? allColumns.filter((column) => !hiddenFields.has(column.field))
    : allColumns;
  const groups = chunkGroups(columns);

  const sheet = workbook.addWorksheet(SHEET_NAME);

  const groupRow: (string | null)[] = groups.flatMap((group) => [
    group.name ?? '',
    ...Array.from({ length: group.span - 1 }, () => null),
  ]);
  sheet.addRow(groupRow);

  const headerRow = columns.map((column) => column.header);
  sheet.addRow(headerRow);

  groups.reduce((offset, group) => {
    if (group.name !== null && group.span > 1) {
      sheet.mergeCells(1, offset, 1, offset + group.span - 1);
    }
    return offset + group.span;
  }, 1);

  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).alignment = { horizontal: 'center' };
  sheet.getRow(2).font = { bold: true };

  for (const item of report.reportItems) {
    sheet.addRow(columns.map((column) => column.getValue(item)));
  }

  sheet.views = [{ state: 'frozen', ySplit: 2 }];
}
